import React, { useCallback, useState, useEffect } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import { useClientSidePanel } from "../../../../providers/ClientSidePanelProvider";
import SegmentedStepper from "../common/SegmentedStepper";
import { useSelector, useDispatch } from "react-redux";
import { RootStateAnswers } from "../../../questionnaire/store/answers-slice";
import {
  RootStateServiceLogicBuilder,
  serviceLogicBuilderActions,
} from "../../store/service-logic-builder-slice";
import ServiceSelection from "../common/ServiceSelection";
import { ServiceRow } from "../logicBuilder/NewLogicModal";
import {
  getUserSavedServices,
  getUserLabPanels,
  updateCustomerPrescription,
} from "../../../../common/network/Api";
import ScrollableWrapper from "../common/ScrollableWrapper";
import LoadingSpinner from "../../../../common/ui/LoadingSpinner";
import PrescriptionBuilderStep2 from "./PrescriptionBuilderStep2";
import { validateServiceSelection } from "../common/serviceValidation";
import { useAppStore } from "../../../../providers/AppStore";
import { validateStep3 } from "../logicBuilder/LogicBuilderStep3";
import BasicInformationModal from "../common/BasicInformationModal";
import { isBasicInfoValidForLabOrder } from "../../utils/basicInfoValidation";
import { getPrescriptionSaveFailure } from "../../utils/labPanelStatus";
import { getApiValidationReasons } from "../../../../common/utils/apiError";

const CustomerNameFiled = () => {
  const theme: Theme = useTheme();
  const answers = useSelector((state: RootStateAnswers) => state.answers);

  return (
    <div style={styles.customerNameField(theme)}>
      <div style={styles.customerNameLabel(theme)}>Customer</div>
      <div
        style={styles.customerNameValue(theme)}
      >{`${answers.personalInfoInitialAnswers.firstName} ${answers.personalInfoInitialAnswers.lastName}`}</div>
    </div>
  );
};

const ServiceSelectionSkeleton = () => {
  const theme: Theme = useTheme();

  return (
    <div>
      <div style={styles.skeletonMainTitle(theme)} />
      <div style={styles.skeletonSubTitle(theme)} />
      <div style={styles.skeletonWrapper(theme)}>
        <div style={styles.skeletonSectionTitle(theme)} />
        <div style={styles.skeletonRow}>
          <div style={styles.skeletonField(theme)} />
          <div style={styles.skeletonField(theme)} />
          <div style={styles.skeletonIcon(theme)} />
        </div>
        <div style={styles.skeletonButton(theme)} />
      </div>
    </div>
  );
};

interface NewPrescriptionWidgetProps {
  editingPrescription?: any | null;
}

const NewPrescriptionWidget: React.FC<NewPrescriptionWidgetProps> = ({
  editingPrescription,
}) => {
  const theme: Theme = useTheme();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [serviceRows, setServiceRows] = useState<ServiceRow[]>([
    {
      id: Date.now().toString(),
      selectedType: null,
      selectedServiceId: null,
    },
  ]);
  const [services, setServices] = useState<any[]>([]);
  const [labPanels, setLabPanels] = useState<any[]>([]);
  const [originalServices, setOriginalServices] = useState<any[]>([]);
  const [showBasicInfoModal, setShowBasicInfoModal] = useState(false);
  const { closePanel, isOpen, onPrescriptionSaved } = useClientSidePanel();
  const isEditMode = !!editingPrescription;
  const savedServices = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedServices
  );
  const savedLabPanels = useSelector(
    (state: RootStateServiceLogicBuilder) =>
      state.serviceLogicBuilder.savedLabPanels
  );
  const answers = useSelector((state: RootStateAnswers) => state.answers);
  const {
    ASSelectedClient,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
  } = useAppStore();

  const handleServicesDataChange = useCallback((updatedServices: any[]) => {
    setServices(updatedServices);
  }, []);

  const handleLabPanelsDataChange = useCallback((updatedLabPanels: any[]) => {
    setLabPanels(updatedLabPanels);
  }, []);

  // Clear validation errors when serviceRows change
  useEffect(() => {
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  }, [serviceRows]);

  // Reset to step 1 with clean data when panel opens (for new prescription only)
  useEffect(() => {
    if (isOpen && !editingPrescription) {
      setCurrentStep(1);
      setValidationErrors([]);
      setServiceRows([
        {
          id: Date.now().toString(),
          selectedType: null,
          selectedServiceId: null,
        },
      ]);
      setServices([]);
      setLabPanels([]);
      setOriginalServices([]); // Clear original services when creating new
    }
  }, [isOpen, editingPrescription]);

  // Load services and lab panels when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [servicesResponse, labPanelsResponse] = await Promise.all([
          getUserSavedServices(),
          getUserLabPanels(),
        ]);
        dispatch(
          serviceLogicBuilderActions.setSavedServices(
            servicesResponse ? JSON.parse(JSON.stringify(servicesResponse)) : []
          )
        );
        dispatch(
          serviceLogicBuilderActions.setSavedLabPanels(
            labPanelsResponse && Array.isArray(labPanelsResponse)
              ? labPanelsResponse
              : []
          )
        );
      } catch (error) {
        console.warn("Error loading data:", error);
        dispatch(serviceLogicBuilderActions.setSavedServices([]));
        dispatch(serviceLogicBuilderActions.setSavedLabPanels([]));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [dispatch]);

  // Load prescription data when editing
  useEffect(() => {
    if (editingPrescription && isOpen && savedServices.length > 0) {
      // Set services from the prescription
      if (editingPrescription.services) {
        // Store original services - these will be kept and combined with new services
        setOriginalServices(
          JSON.parse(JSON.stringify(editingPrescription.services))
        );

        // Don't pre-populate services in the UI - start with empty state
        // User will add new services, and we'll combine them with existing ones when saving
        setServices([]);
        setServiceRows([
          {
            id: Date.now().toString(),
            selectedType: null,
            selectedServiceId: null,
          },
        ]);

        // Start from step 1 when editing
        setCurrentStep(1);
      }
    }
  }, [editingPrescription, isOpen, savedServices]);

  const handleClose = () => {
    closePanel();
  };

  const labProviders = labPanels.map((lp: any) => lp.panelData?.provider);

  const handleSave = async () => {
    // A prescription that includes a lab panel can only be placed when the
    // client's contact info is complete and the address is in a supported
    // (US) country and a state each panel's lab serves; otherwise prompt to
    // fix it first.
    if (
      labPanels.length > 0 &&
      !isBasicInfoValidForLabOrder(answers.personalInfoAnswers, labProviders)
    ) {
      setShowBasicInfoModal(true);
      return;
    }
    try {
      setIsSaving(true);

      // Prepare data to send
      let dataToSend;

      // Build lab panels payload. A panel with order-entry questions carries
      // them in full, each with the `userAnswer` typed in the editor.
      const labPanelsPayload = labPanels.map((lp: any) => ({
        userLabPanelID: lp.userLabPanelID,
        startDate: lp.startDate,
        ...(Array.isArray(lp.questions) && lp.questions.length > 0
          ? { questions: lp.questions }
          : {}),
      }));

      if (isEditMode && editingPrescription) {
        // For edit mode, combine original services with new services
        // New services don't have IDs, original services have IDs
        const combinedServices = [
          ...originalServices, // Existing services from the prescription
          ...services, // New services added by the user
        ];

        dataToSend = {
          ...editingPrescription,
          services: combinedServices,
          labPanels: [
            ...(editingPrescription.labPanels || []),
            ...labPanelsPayload,
          ],
        };
      } else {
        // For new prescription, create new object
        dataToSend = {
          services: services,
          labPanels: labPanelsPayload,
          CustomerID: ASSelectedClient ? ASSelectedClient.id : "",
          sourceType: 3,
        };
      }

      // Call API
      const saved = await updateCustomerPrescription(dataToSend);

      // The backend charges the lab panels as part of the save and still
      // answers 200 when a charge is refused (the panel comes back on a failure
      // status with the reason in statusLog). Tell the affiliate the
      // prescription was saved but the payment was not, instead of the plain
      // success line; the Retry lives in the prescription details.
      const paymentFailure = getPrescriptionSaveFailure(saved);
      ASSetOpenSnackBar(true);
      if (paymentFailure) {
        ASSetSnackBarMessage(paymentFailure);
        ASSetTypeSnackBar("alert");
      } else {
        ASSetSnackBarMessage({
          title: "Success",
          message: "Prescription saved successfully!",
        });
        ASSetTypeSnackBar("success");
      }

      // Call callback to refetch prescription data
      if (onPrescriptionSaved) {
        onPrescriptionSaved();
      }

      // Close panel after successful save
      handleClose();
    } catch (error: any) {
      console.error("Error saving prescription:", error);

      const responseMessage = error?.response?.data?.message;
      // A 422 on the lab's order-entry answers lists what is wrong per
      // question ("'WHEN VISITED (MM/DD/YYYY)' must be a date as
      // MM/DD/YYYY"); those reasons are the message, under the backend's
      // headline, so the affiliate knows which answer to fix.
      const validationReasons = getApiValidationReasons(error);

      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage(
        validationReasons.length > 0
          ? {
              title: responseMessage || "Some answers are not valid",
              message: validationReasons.join("\n"),
            }
          : {
              title: "Error",
              message: responseMessage
                ? responseMessage.replace(/\.\s*/g, ".\n")
                : "Failed to save prescription. Please try again.",
            }
      );
      ASSetTypeSnackBar("alert");
    } finally {
      setIsSaving(false);
    }
  };

  const handleStepValidation = useCallback(
    (step: number): boolean => {
      if (step === 1) {
        const validation = validateServiceSelection(serviceRows);
        setValidationErrors(validation.errors);
        return validation.isValid;
      }
      if (step === 2) {
        const validation = validateStep3(
          services,
          "prescriptionBuilder",
          isEditMode ? originalServices : undefined,
          labPanels
        );
        setValidationErrors(validation.errors);
        return validation.isValid;
      }
      return true;
    },
    [serviceRows, services, labPanels, isEditMode, originalServices]
  );

  return (
    <div style={styles.container}>
      <div style={styles.headerContainer}>
        <div style={styles.headerTitle(theme)}>
          {isEditMode ? "Add new service" : "New Prescription"}
        </div>
        <div onClick={handleClose} style={styles.iconWrapper}>
          <XIconSvg />
        </div>
      </div>
      <div style={styles.content}>
        {isSaving ? (
          // Saving in progress: lock the whole panel behind a centered spinner
          // until it closes on success — the prescription-list PageLoader takes
          // over after close — rather than only disabling the Save button.
          <div style={styles.savingLoader}>
            <LoadingSpinner
              size="large"
              color={theme.colors_ds.PRIMARY_ICON_MAIN}
            />
          </div>
        ) : (
          <SegmentedStepper
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            isNextButtonDisabled={false}
            handleSave={handleSave}
            isSaveDisabled={isSaving}
            startStep={1}
            totalSteps={2}
            onStepValidation={handleStepValidation}
            onStepChange={(newStep, direction) => {
              // Only clear errors when going back, not forward
              if (direction === "back") {
                setValidationErrors([]);
              }
              setCurrentStep(newStep);
            }}
            validationErrors={validationErrors}
          >
            <ScrollableWrapper>
              <div style={styles.scrollableContent}>
                {/*<CustomerNameFiled />*/}
                {currentStep === 1 &&
                  (isLoading ? (
                    <ServiceSelectionSkeleton />
                  ) : (
                    <div>
                      <div style={styles.title(theme)}>
                        Define Service Outcomes
                      </div>
                      <div style={styles.subTitle(theme)}>
                        Add one or more services that will be included in this
                        prescription.
                      </div>
                      <ServiceSelection
                        serviceRows={serviceRows}
                        setServiceRows={setServiceRows}
                        savedServices={savedServices}
                        savedLabPanels={savedLabPanels}
                        addButtonLabel="Add service"
                      />
                    </div>
                  ))}
                {currentStep === 2 && (
                  <PrescriptionBuilderStep2
                    serviceRows={serviceRows}
                    services={services}
                    onServicesDataChange={handleServicesDataChange}
                    labPanels={labPanels}
                    savedLabPanels={savedLabPanels}
                    onLabPanelsDataChange={handleLabPanelsDataChange}
                  />
                )}
              </div>
            </ScrollableWrapper>
          </SegmentedStepper>
        )}
      </div>
      <BasicInformationModal
        open={showBasicInfoModal}
        clientId={ASSelectedClient ? ASSelectedClient.id : ""}
        labProviders={labProviders}
        onClose={() => setShowBasicInfoModal(false)}
        onSaved={() => {
          setShowBasicInfoModal(false);
          handleSave();
        }}
      />
    </div>
  );
};

export default NewPrescriptionWidget;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "100%",
    width: "100%",
    padding: "16px",
  },
  headerContainer: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    marginBottom: "16px",
  },
  headerTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "34px",
    flex: 1,
  }),
  iconWrapper: {
    marginTop: "10px",
    cursor: "pointer",
  },
  content: {
    flex: 1,
    overflowY: "auto" as "auto",
  },
  savingLoader: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  scrollableContent: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    paddingBottom: "8px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  subTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    marginBottom: "16px",
  }),
  customerNameField: (theme: Theme) => ({
    padding: "8px 12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    marginBottom: "8px",
  }),
  customerNameLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
    lineHeight: "16px",
  }),
  customerNameValue: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  skeletonMainTitle: (theme: Theme) => ({
    height: "20px",
    width: "180px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
    marginBottom: "8px",
  }),
  skeletonSubTitle: (theme: Theme) => ({
    height: "17px",
    width: "320px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
    marginBottom: "16px",
  }),
  skeletonWrapper: (theme: Theme) => ({
    padding: "16px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  }),
  skeletonSectionTitle: (theme: Theme) => ({
    height: "20px",
    width: "150px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
  }),
  skeletonRow: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    width: "100%",
  },
  skeletonField: (theme: Theme) => ({
    height: "40px",
    flex: 1,
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
  }),
  skeletonIcon: (theme: Theme) => ({
    height: "24px",
    width: "24px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
  }),
  skeletonButton: (theme: Theme) => ({
    height: "40px",
    width: "120px",
    backgroundColor: theme.colorTokens.N[20],
    borderRadius: "4px",
    animation: "pulse 1.5s ease-in-out infinite",
  }),
};
